import { RemoteThreadHeader } from "./RemoteThreadHeader";
import type {
  RemoteThreadDispatchSettings,
  RemoteThreadEnvironmentWaitingReason,
} from "./useRemoteThreadExecutionIntent";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { ThreadId, type EnvironmentId } from "@cmmd-forge/contracts";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";

import {
  cmmdRuntimeThreadReplicaResource,
  useCmmdRuntimeThreadReplicaSnapshot,
} from "../../cmmd/cmmdRuntimeThreadReplicaResource";
import { SidebarInset } from "../ui/sidebar";
import { RemoteRuntimeThreadView } from "./RemoteRuntimeThreadView";
import { toRemoteRuntimeThreadViewState } from "./remoteRuntimeThreadRoute.logic";
import type { RemoteRuntimeConnectionState } from "./remoteRuntimeThreadConnection.logic";
import { useRemoteThreadComposerContext } from "./useRemoteThreadComposerContext";
import { useComposerDraftStore } from "../../composerDraftStore";

export interface RemoteRuntimeThreadRouteProps {
  readonly environmentId: EnvironmentId;
  readonly runtimeId: string;
  readonly threadId: string;
  readonly fallbackTitle: string;
  readonly fallbackRuntimeName: string;
  readonly connectionState: RemoteRuntimeConnectionState;
  readonly environmentWaitingReason: RemoteThreadEnvironmentWaitingReason | null;
  readonly historyOnly: boolean;
  readonly onExecutionIntent: (settings?: RemoteThreadDispatchSettings) => void;
  readonly onRetryConnection: () => void;
}

const toViewState = toRemoteRuntimeThreadViewState;
const PASSIVE_HISTORY_REFRESH_INTERVAL_MS = 5_000;

/** Shows an authorized Cloud thread and refreshes its saved transcript while it is passive. */
export function RemoteRuntimeThreadRoute(props: RemoteRuntimeThreadRouteProps) {
  const snapshot = useCmmdRuntimeThreadReplicaSnapshot(props.runtimeId, props.threadId);
  const threadRef = scopeThreadRef(props.environmentId, ThreadId.make(props.threadId));
  const replica = snapshot.kind === "idle" ? null : snapshot.replica;
  const composerContext = useRemoteThreadComposerContext({
    threadRef,
    runtimeId: props.runtimeId,
    replica: replica ?? null,
  });
  const transcriptKey = `${props.runtimeId}\0${props.threadId}`;
  const validationAttemptRef = useRef(0);
  const loadInFlightRef = useRef<{ readonly key: string; readonly token: number } | null>(null);
  const [validatedHistoryKey, setValidatedHistoryKey] = useState<string | null>(null);
  /** Coalesces passive reads unless Retry explicitly requests a fresh read. */
  const loadHistory = (
    options: { readonly force?: boolean; readonly invalidateHistory?: boolean } = {},
  ) => {
    const key = transcriptKey;
    if (!options.force && loadInFlightRef.current?.key === key) return;
    const token = validationAttemptRef.current + 1;
    loadInFlightRef.current = { key, token };
    const attempt = ++validationAttemptRef.current;
    if (props.historyOnly && options.invalidateHistory !== false) setValidatedHistoryKey(null);
    void cmmdRuntimeThreadReplicaResource
      .load(props.runtimeId, props.threadId)
      .then(() => {
        if (validationAttemptRef.current !== attempt) return;
        if (props.historyOnly) setValidatedHistoryKey(transcriptKey);
      })
      .catch(() => {})
      .finally(() => {
        if (loadInFlightRef.current?.token === token) {
          loadInFlightRef.current = null;
        }
      });
  };
  const loadOnMount = useEffectEvent(loadHistory);
  /** Invalidates an effect's result so an old request cannot validate a different view. */
  const invalidateEffectRequest = (): void => {
    validationAttemptRef.current += 1;
    if (loadInFlightRef.current?.key === transcriptKey) {
      loadInFlightRef.current = null;
    }
  };
  const invalidateEffectRequestOnCleanup = useEffectEvent(invalidateEffectRequest);
  useEffect(() => {
    loadOnMount({ invalidateHistory: true });
    if (props.connectionState.kind === "live") {
      return invalidateEffectRequestOnCleanup;
    }
    if (props.historyOnly) {
      return invalidateEffectRequestOnCleanup;
    }
    /** Refreshes only when the viewer can see the passive thread. */
    const refresh = (): void => {
      if (document.visibilityState === "hidden") return;
      loadOnMount({ invalidateHistory: false });
    };
    const intervalId = window.setInterval(refresh, PASSIVE_HISTORY_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      invalidateEffectRequestOnCleanup();
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [
    props.connectionState.kind,
    props.historyOnly,
    props.runtimeId,
    props.threadId,
    transcriptKey,
  ]);

  const retry = () => {
    if (
      props.connectionState.kind === "failed" &&
      useComposerDraftStore.getState().getComposerDraft(threadRef)?.stagedSubmission
    ) {
      props.onRetryConnection();
    }
    loadHistory({ force: true, invalidateHistory: true });
  };

  const canRenderSnapshot = !props.historyOnly || validatedHistoryKey === transcriptKey;

  return (
    <SidebarInset className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <RemoteThreadHeader
        authorized={canRenderSnapshot}
        threadRef={threadRef}
        runtimeId={props.runtimeId}
      />
      <div className="min-h-0 flex-1">
        <RemoteRuntimeThreadView
          composerContext={composerContext}
          state={canRenderSnapshot ? toViewState(snapshot) : { kind: "loading" }}
          connectionState={props.connectionState}
          environmentWaitingReason={props.environmentWaitingReason}
          environmentId={props.environmentId}
          runtimeId={props.runtimeId}
          threadRef={scopeThreadRef(props.environmentId, ThreadId.make(props.threadId))}
          fallbackTitle={props.fallbackTitle}
          fallbackRuntimeName={props.fallbackRuntimeName}
          historyOnly={props.historyOnly}
          onExecutionIntent={props.onExecutionIntent}
          onRetry={retry}
          onRefreshHistory={loadHistory}
        />
      </div>
    </SidebarInset>
  );
}
